import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material'
import moment from 'moment/moment'
import lightTheme from '../../styles/theme/lightTheme'
import * as React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faCreditCard,
} from '@fortawesome/pro-regular-svg-icons'
import { useTranslation } from 'react-i18next'

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
}

const TablePanelBox = styled(TabPanel)(({ theme }) => ({
  height: 280,
  backgroundColor: 'rgb(227, 230, 255, 0.5)',
  '& .MuiBox-root': {
    padding: 0,
  },
}))

const HeaderTab = styled(TableHead)(({ theme }) => ({
  height: 50,
  backgroundColor: lightTheme.palette.primary.b6,
}))

const TitleHead = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const BodyTable = styled(TableBody)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: '2px solid rgb(227, 230, 255, 0.5)',
  fontFamily: 'Century Gothic',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const getFormattedPrice = (offre) => {
  const currencyData =
    typeof window !== 'undefined' && sessionStorage.getItem('currency')
      ? JSON.parse(sessionStorage.getItem('currency'))
      : null
  const rate = currencyData?.currentRate || 1
  const devise = currencyData?.devise || 'CHF'
  return `${devise} ${(offre.prixPromo * rate).toFixed(2)}`
}

const getStatusConfig = (offre) => {
  if (offre.status === 'payer') {
    return {
      label: 'Paid',
      color: lightTheme.palette.success.main,
      icon: faCheck,
      canPay: false,
    }
  }

  return {
    label: 'Pending',
    color: lightTheme.palette.primary.main,
    icon: faCreditCard,
    canPay: true,
  }
}

const renderStatus = (offre) => {
  const status = getStatusConfig(offre)
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        color: status.color,
        fontWeight: 600,
      }}
    >
      <FontAwesomeIcon icon={status.icon} fontSize={14} />
      <Typography variant="body2">{status.label}</Typography>
    </Box>
  )
}

const OffreTable = ({ offres, handlePayementOffre }) => {
  const { t } = useTranslation()

  return (
    <TablePanelBox>
      {offres && (
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table
            stickyHeader
            aria-label="offers table"
            sx={{ minWidth: { xs: 320, sm: 'auto' } }}
          >
            <HeaderTab>
              <TableRow>
                <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {t('profile.date')}
                </TitleHead>
                <TitleHead>{t('header.offer')}</TitleHead>
                <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {t('profile.price')}
                </TitleHead>
                <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  {t('profile.status')}
                </TitleHead>
                <TitleHead
                  sx={{
                    width: { xs: 56, sm: 120 },
                    textAlign: 'center',
                  }}
                >
                  {t('profile.actions')}
                </TitleHead>
              </TableRow>
            </HeaderTab>
            <BodyTable>
              {offres && offres.length > 0 ? (
                offres.map((offre, i) => (
                  <TableRow
                    key={i}
                    sx={{
                      '&:hover': {
                        backgroundColor: lightTheme.palette.primary.b5,
                      },
                    }}
                  >
                    <StyledTableCell
                      sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                    >
                      {moment(offre.date)
                        .tz('Europe/Paris')
                        .format('DD/MM/YYYY HH:mm')}
                    </StyledTableCell>
                    <StyledTableCell sx={{ minWidth: { xs: 180, sm: 'auto' } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#334155',
                            fontWeight: 600,
                          }}
                        >
                          {offre.offre.titre}
                        </Typography>
                        <Box
                          sx={{
                            display: { xs: 'flex', sm: 'none' },
                            flexDirection: 'column',
                            gap: 0.5,
                            pt: 1,
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {t('profile.date')}:
                            </Typography>
                            <Typography variant="caption">
                              {moment(offre.date)
                                .tz('Europe/Paris')
                                .format('DD/MM/YYYY')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {t('profile.price')}:
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {getFormattedPrice(offre)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {renderStatus(offre)}
                          </Box>
                        </Box>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getFormattedPrice(offre)}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                    >
                      {renderStatus(offre)}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: 1,
                        }}
                      >
                        {offre.status !== 'payer' && (
                          <Tooltip title={t('button.pay')} arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.preventDefault()
                                handlePayementOffre(offre)
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faCreditCard}
                                fontSize={16}
                                color={lightTheme.palette.primary.b2}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2">
                      {t('dashboard.noOffers', 'Aucune offre')}
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              )}
            </BodyTable>
          </Table>
        </Box>
      )}
    </TablePanelBox>
  )
}

export default OffreTable
